import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Toolbar,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
} from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import { API } from "../../../utils/Api";

function OnlineSalesList() {
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Filters
  const [platform, setPlatform] = useState("ALL");
  const [startDate, setStartDate] = useState(""); // yyyy-mm-dd
  const [endDate, setEndDate] = useState(""); // yyyy-mm-dd
  const [search, setSearch] = useState("");

  const getSales = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API.BASE_URL}third-party-sales`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const list = res?.data?.data?.dbResponse ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error(error);
      setErr(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load third party sales."
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const platforms = useMemo(() => {
    const set = new Set((rows || []).map((r) => r?.platform).filter(Boolean));
    return ["ALL", ...Array.from(set)];
  }, [rows]);

  // Helpers
  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

  const inDateRange = (rowIso) => {
    if (!startDate && !endDate) return true;
    if (!rowIso) return false;

    const rowDay = new Date(rowIso);
    if (Number.isNaN(rowDay.getTime())) return false;

    const rowYMD = rowDay.toISOString().slice(0, 10);
    if (startDate && rowYMD < startDate) return false;
    if (endDate && rowYMD > endDate) return false;
    return true;
  };

  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();

    return (rows || []).filter((r) => {
      const matchesPlatform =
        platform === "ALL" ? true : r?.platform === platform;

      const dateIso = r?.createdAt || r?.date;
      const matchesRange = inDateRange(dateIso);

      const productName = r?.product?.name || "";
      const outletName = r?.outlet?.name || "";
      const addonText = (r?.addons || [])
        .map((a) => `${a?.addonTitle || ""} ${a?.addonValueTitle || ""}`.trim())
        .join(" | ");

      const matchesSearch = !s
        ? true
        : `${productName} ${outletName} ${addonText} ${r?.platform || ""}`
          .toLowerCase()
          .includes(s);

      return matchesPlatform && matchesRange && matchesSearch;
    });
  }, [rows, platform, startDate, endDate, search]);

  const summary = useMemo(() => {
    const totalOrders = filteredRows.length;
    const totalQty = filteredRows.reduce(
      (acc, r) => acc + (Number(r?.quantity) || 0),
      0
    );
    const gross = filteredRows.reduce(
      (acc, r) => acc + (Number(r?.price) || 0),
      0
    );
    const discount = filteredRows.reduce(
      (acc, r) => acc + (Number(r?.discount) || 0),
      0
    );
    const net = filteredRows.reduce(
      (acc, r) => acc + (Number(r?.total) || 0),
      0
    );
    return { totalOrders, totalQty, gross, discount, net };
  }, [filteredRows]);

  const platformChipSx = (p = "") => {
    const key = String(p).toLowerCase();

    // Zomato red
    if (key.includes("zomato")) {
      return {
        backgroundColor: "#E23744",
        color: "#fff",
        borderColor: "#E23744",
        "& .MuiChip-label": { color: "#fff" },
      };
    }

    // Swiggy orange
    if (key.includes("swiggy")) {
      return {
        backgroundColor: "#FC8019",
        color: "#fff",
        borderColor: "#FC8019",
        "& .MuiChip-label": { color: "#fff" },
      };
    }

    // Default theme
    return {
      backgroundColor: "#003049",
      color: "#fff",
      borderColor: "#003049",
      "& .MuiChip-label": { color: "#fff" },
    };
  };

  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          {/* Header */}
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                    Third Party Sales
                  </Typography>
                </Box>

                <Chip
                  icon={<FilterAltOutlined />}
                  label={`Records: ${filteredRows?.length || 0}`}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Search
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search for product, outlet, addon, platform"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Platform
                  </Typography>

                  <FormControl
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}
                  >
                    <Select value={platform} onChange={(e) => setPlatform(e.target.value)} displayEmpty>
                      {platforms.map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    From Date
                  </Typography>

                  <TextField
                    fullWidth
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    To Date
                  </Typography>

                  <TextField
                    fullWidth
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={1}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8, textAlign: "center" }}>
                    Refresh
                  </Typography>

                  <Chip
                    onClick={getSales}
                    label={loading ? "..." : "Get"}
                    sx={{
                      width: "100%",
                      height: 52,
                      borderRadius: 2,
                      cursor: "pointer",
                      fontWeight: 900,
                      backgroundColor: "#003049",
                      color: "#fff",
                      "& .MuiChip-label": { color: "#fff" },
                      "&:hover": { backgroundColor: "#00263a" },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip icon={<FilterAltOutlined />} label={`Orders: ${summary.totalOrders}`} variant="outlined" sx={{ fontWeight: 800, borderRadius: 2 }} />
                    <Chip label={`Qty: ${summary.totalQty}`} variant="outlined" sx={{ fontWeight: 800, borderRadius: 2 }} />
                    <Chip label={`Gross: ${money(summary.gross)}`} variant="outlined" sx={{ fontWeight: 800, borderRadius: 2 }} />
                    <Chip label={`Discount: ${money(summary.discount)}`} variant="outlined" sx={{ fontWeight: 800, borderRadius: 2 }} />
                  </Stack>

                  <Chip
                    label={`Net: ${money(summary.net)}`}
                    sx={{
                      fontWeight: 900,
                      borderRadius: 2,
                      backgroundColor: "#003049",
                      color: "#fff",
                      "& .MuiChip-label": { color: "#fff" },
                    }}
                  />
                </Stack>
              </Box>

              {/* Table */}
              {loading ? (
                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 700 }}>Loading Sales...</Typography>
                </Box>
              ) : err ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#b91c1c" }}>
                    {err}
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Please check token / API and try again.
                  </Typography>
                </Box>
              ) : filteredRows?.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                    No third party sales to display
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Try changing filters or search.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 0,
                      maxHeight: "70vh",
                      overflow: "auto",
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {["No.", "Date", "Platform", "Product", "Qty", "Price", "Discount", "Total"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                              align={["Product", "Platform", "Date"].includes(h) ? "left" : "center"}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredRows.map((r, index) => {
                          const productName = r?.product?.name || "N/A";
                          const outletName = r?.outlet?.name || "N/A";
                          const addons = Array.isArray(r?.addons) ? r.addons : [];

                          return (
                            <TableRow
                              key={r?._id || index}
                              hover
                              sx={{
                                backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                "& td": { borderBottom: "1px solid #f1f5f9" },
                              }}
                            >
                              <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                                {index + 1}
                              </TableCell>

                              <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
                                  {formatDateTime(r?.createdAt || r?.date)}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                  {outletName}
                                </Typography>
                              </TableCell>

                              <TableCell align="left">
                                <Chip
                                  size="small"
                                  label={r?.platform || "N/A"}
                                  sx={{
                                    fontWeight: 900,
                                    borderRadius: 2,
                                    ...platformChipSx(r?.platform),
                                  }}
                                />
                              </TableCell>

                              {/* ✅ Product + Addons in same cell */}
                              <TableCell align="left" sx={{ minWidth: 320 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a" }}>
                                  {productName}
                                </Typography>

                                {addons.length === 0 ? (
                                  <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.3 }}>
                                    No addons
                                  </Typography>
                                ) : (
                                  <Stack direction="row" flexWrap="wrap" gap={0.6} sx={{ mt: 0.6 }}>
                                    {addons.map((a, i) => (
                                      <Chip
                                        key={a?._id || i}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          borderRadius: 2,
                                          fontWeight: 700,
                                          height: 22,
                                          "& .MuiChip-label": { px: 0.75, fontSize: 11 },
                                        }}
                                        label={`${a?.addonTitle || ""}: ${a?.addonValueTitle || ""}`}
                                      />
                                    ))}
                                  </Stack>
                                )}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 900 }}>
                                {Number(r?.quantity || 0)}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {money(r?.price)}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {money(r?.discount)}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                {money(r?.total)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default OnlineSalesList;
